import { useEffect, useContext, useRef, useMemo } from 'react';
import ABeagleContext from '../../contexts/abeagle';
import initCrashMonitor from '../../utils/crash-monitor';
import { getUserUUID } from '@buzzfeed/bf-utils/lib/userid';
import cookies from '@buzzfeed/bf-utils/lib/cookies';
import { getSessionUUID } from '@buzzfeed/bf-utils/lib/sessionid';
import getPageSessionIds from '@buzzfeed/bf-utils/lib/page-session-ids';
import { buzzCommonLayerData } from './client-event-tracking';

/**
 * Unified crash reporting hook that implements two complementary strategies:
 *
 * 1. HEARTBEAT MONITORING (Active Detection)
 *    - Uses a service worker + heartbeat polling to detect page crashes
 *    - When the main thread stops responding, the service worker reports the crash
 *    - Managed via `initCrashMonitor` utility
 *
 * 2. CRASH REPORT STORAGE API (Passive Context)
 *    - Uses the browser's native Reporting API
 *    - Stores application context that gets attached to browser-generated crash reports using the
 *      Crash Report Storage API (Chrome Origin Trial)
 *    - Data is ephemeral (document lifetime) and only sent when crashes occur
 *    - Origin Trial: https://developer.chrome.com/origintrials/#/view_trial/1304355042077179905
 *    - Explainer: https://github.com/WICG/crash-reporting/blob/main/crashReport-explainer.md
 *
 * Both strategies share the same context data (user/session IDs, page info) to ensure
 * consistent crash diagnostics regardless of which mechanism captures the crash.
 *
 * @param {Object} buzz - The buzz/article data object
 */
export function useCrashReporting(buzz) {
  const { experiments, getFeatureFlagValue } = useContext(ABeagleContext);
  const isQuiz = buzz.isQuiz || buzz.isBFPQuiz;
  const heartbeatMonitorRef = useRef(null);

  // Shared context data used by both crash reporting strategies
  const crashContext = useMemo(() => {
    // Return empty object on server-side rendering
    if (typeof window === 'undefined') {
      return {};
    }

    const url = `${window.location.origin}${window.location.pathname}`;
    const { page_session_id, previous_page_session_id } = getPageSessionIds(url);

    return {
      ...buzzCommonLayerData(buzz)(),
      random_user_uuid: cookies.get('user_uuid', 'unauthenticated'),
      client_session_id: getSessionUUID(),
      page_session_id,
      previous_page_session_id,
      client_uuid: getUserUUID(),
    };
  }, [buzz]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STRATEGY 1: Heartbeat + Service Worker Crash Detection
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!experiments.loaded) return;

    // Cleanup previous instance if it exists
    if (heartbeatMonitorRef.current) {
      heartbeatMonitorRef.current.cleanup();
      heartbeatMonitorRef.current = null;
    }

    const heartbeatConfig = {
      buzzId: crashContext.context_page_id,
      clientSessionId: crashContext.client_session_id,
      clientUuid: crashContext.client_uuid,
      destination: crashContext.destination,
      isQuiz,
      pageSessionId: crashContext.page_session_id,
      previousPageSessionId: crashContext.previous_page_session_id,
      randomUserUuid: crashContext.random_user_uuid,
      service: 'bf_bpage_ui',
    };

    initCrashMonitor(heartbeatConfig, getFeatureFlagValue)
      .then((monitor) => {
        heartbeatMonitorRef.current = monitor;
      })
      .catch((err) => {
        console.error('[useCrashReporting] Failed to initialize heartbeat crash monitor:', err);
      });

    return () => {
      if (heartbeatMonitorRef.current) {
        heartbeatMonitorRef.current.cleanup();
        heartbeatMonitorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashContext, experiments.loaded]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STRATEGY 2: Native Browser Crash Report Storage API (Progressive Enhancement)
  // ─────────────────────────────────────────────────────────────────────────────
  // This is a progressive enhancement that gracefully degrades when unavailable.
  // Currently requires BOTH:
  //   - Chrome Origin Trial enrollment (automatic for enrolled domains)
  //   - chrome://flags/#enable-experimental-web-platform-features enabled
  // Once the Crash Report Storage API becomes a standard feature, this will work
  // across all supporting browsers without additional configuration.
  //
  // API: crashReport.initialize(bufferSizeInBytes) — allocates shared memory buffer
  // Data is JSON-serialized as {"key":"value",...} so account for JSON overhead
  // Buffer size: 8KB allows for full context including long referrer URLs
  useEffect(() => {
    const crashReport = window?.crashReport;
    if (!crashReport) return;

    let cancelled = false;
    const storedKeys = [];

    // 8KB should be sufficient for the current full crashContext including long referrer URLs.
    const SIZE = 8192;

    (async () => {
      try {
        // initialize(size) allocates a buffer of `size` bytes for crash data.
        if (typeof crashReport.initialize === 'function') {
          await crashReport.initialize(SIZE);
        }

        if (cancelled) {
          return;
        }

        // Store full context data that will be attached to browser crash reports
        for (const [key, value] of Object.entries(crashContext)) {
          if (value !== undefined && value !== null) {
            try {
              crashReport.set(key, String(value));
              storedKeys.push(key);
            } catch (err) {
              // Buffer full or value issue — log and stop
              console.warn(`[useCrashReporting] crashReport.set("${key}") failed:`, err.message);
              break;
            }
          }
        }
      } catch (err) {
        console.warn('[useCrashReporting] crashReport.initialize() failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      // Cleanup stored keys (initialize() already resolved, don't call again)
      try {
        storedKeys.forEach((key) => crashReport.delete(key));
      } catch {
        // ignore cleanup errors
      }
    };
  }, [crashContext]);
}
